import type { IntegrationKind } from '@developerz/domain';
import { IconAlert, IconCircleCheck } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { ConnectInput, IntegrationRow, IntegrationsData } from '../lib/integrations';
import { CardGridSkeleton } from './CardGridSkeleton';
import { ConnectIntegrationModal } from './ConnectIntegrationModal';
import { ConnectionTokens } from './ConnectionTokens';
import { DisconnectIntegrationModal } from './DisconnectIntegrationModal';
import { EmptyState } from './EmptyState';
import { IntegrationCard } from './IntegrationCard';
import { IntegrationUsage } from './IntegrationUsage';
import { PageHeader } from './PageHeader';
import { QueryBoundary, type QueryBoundaryProps } from './QueryBoundary';
import { Toast } from './Toast';

// Presenter for the Integrations route (mock Integrations()): header → OAuth
// landing banner → md:3 catalog grid → Connections panel. All state + handlers
// live in the `Integrations` route container.

export type IntegrationsViewProps = {
  readonly hasSession: boolean;
  readonly token: string | null;
  readonly query: QueryBoundaryProps<IntegrationsData>['query'];
  readonly kinds: readonly IntegrationKind[];
  readonly landingConnected: IntegrationKind | null;
  readonly landingError: string | null;
  readonly connecting: IntegrationKind | null;
  readonly busy: boolean;
  readonly connectError: string | null;
  readonly onConnect: (kind: IntegrationKind) => void;
  readonly onConnectOauth: (kind: IntegrationKind) => void;
  readonly onSubmitConnect: (input: ConnectInput) => void;
  readonly onCloseConnect: () => void;
  readonly disconnecting: IntegrationRow | null;
  readonly onAskDisconnect: (row: IntegrationRow) => void;
  readonly onConfirmDisconnect: () => void;
  readonly onCloseDisconnect: () => void;
  readonly toast: string | null;
  readonly onToastDismiss: () => void;
};

export function IntegrationsView(props: IntegrationsViewProps): JSX.Element {
  return (
    <>
      <PageHeader title={t('integrations.title')} desc={t('integrations.subtitle')} />

      <Show when={props.landingConnected}>
        {(kind) => (
          <div class="alert alert--icon" role="status">
            <IconCircleCheck />
            <p class="alert__title">
              {t('integrations.connect_success', { name: t(`integrations.kind.${kind()}`) })}
            </p>
          </div>
        )}
      </Show>
      <Show when={props.landingError}>
        {(message) => (
          <div class="alert alert--icon alert--destructive" role="alert">
            <IconAlert />
            <p class="alert__title">{message()}</p>
          </div>
        )}
      </Show>

      <Show
        when={props.hasSession}
        fallback={<EmptyState message={t('integrations.no_session')} />}
      >
        <QueryBoundary
          query={props.query}
          isEmpty={() => false}
          loading={<CardGridSkeleton listClass="integrations__grid" />}
        >
          {(page) => {
            const byKind = new Map(page.data.map((r) => [r.kind, r] as const));
            return (
              <ul class="integrations__grid">
                <For each={props.kinds}>
                  {(kind) => (
                    <IntegrationCard
                      kind={kind}
                      row={byKind.get(kind) ?? null}
                      oauthCapable={page.oauth_kinds.includes(kind)}
                      onConnect={props.onConnect}
                      onConnectOauth={props.onConnectOauth}
                      onDisconnect={props.onAskDisconnect}
                    />
                  )}
                </For>
              </ul>
            );
          }}
        </QueryBoundary>

        {/* #1222: per-call integration audit feed (#309 gap). Sits between the
            catalog grid and the vault-secrets panel — same row shape as the
            account audit feed (#854). Tenant fence is server-side; this view
            only renders authorised rows. */}
        <IntegrationUsage token={props.token} />

        {/* Connections manager (#312): dashboard-managed vault credentials for
            external MCP servers + APIs — one connector surface, two resources. */}
        <ConnectionTokens token={props.token} />

        <ConnectIntegrationModal
          kind={props.connecting}
          busy={props.busy}
          error={props.connectError}
          onSubmit={props.onSubmitConnect}
          onClose={props.onCloseConnect}
        />

        <DisconnectIntegrationModal
          row={props.disconnecting}
          onConfirm={props.onConfirmDisconnect}
          onClose={props.onCloseDisconnect}
        />
      </Show>

      <Toast message={props.toast} tone="error" onDismiss={props.onToastDismiss} />
    </>
  );
}
